import {Project} from '~/types';
import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import QueryBuilder from "./queryBuilder";
import FileTransfer from "./fileTransfer";
import {getFileNameFromUrl} from "~/user/helpers";
import { getLastUsedProjectIds, hasPendingSyncForProject } from '~/sqlite/lastUsedCache';
import { idsAbsentFromIncoming } from '~/data/local/diffHelper';

export class ProjectOfflineService {

    constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

    private readonly qb: QueryBuilder = new QueryBuilder();
    private get SQLite() {
        return resolveOfflineDb(this.databaseClient);
    }

    public reset(projects: Project[]) {
        if (this.SQLite) {
            console.log('downloadImages')
            return new Promise((resolve, reject) => {
                this.downloadProjectsImages(projects).then(async ({projects, deleteProjects}) => {
                    const db = this.SQLite;
                    const protectedIds = new Set(getLastUsedProjectIds());
                    const candidateIds = deleteProjects
                        .map((data) => String(data.id))
                        .filter((id) => !protectedIds.has(id));
                    const deleteCondition: string[] = [];
                    for (const id of candidateIds) {
                        const hasPending = await hasPendingSyncForProject(id);
                        if (!hasPending) {
                            deleteCondition.push(id);
                        }
                    }
                    db.transaction((tx) => {
                        if (deleteCondition.length) {
                            const remove = this.qb.deleteIn('Project', 'id', deleteCondition);
                            tx.executeSql(remove.query, remove.params);
                        }
                        if (projects.length) {
                            const insert = this.qb.insertOrReplace('Project', projects);
                            tx.executeSql(insert.query, insert.params);
                        }
                    }, (e) => reject(e), () => resolve(undefined));
                });
            });
        }
        return Promise.resolve();
    }

    public downloadProjectsImages(projects: Project[]): Promise<{projects: Project[], deleteProjects: Project[]}> {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve({ projects: [] as Project[], deleteProjects: [] as Project[] });
        }

        const allPromise: Promise<Project>[] = [];
        const { query, params } = this.qb.select('Project');

        return new Promise<{projects: Project[], deleteProjects: Project[]}>((resolve, reject) => {
            db.executeSql(query, params, (resultSet) => {
                const projectsWithoutUrl = projects.map(
                    (data) => (
                        {
                            ...data,
                            image: data.image ? `projectImages/${getFileNameFromUrl(data.image)}` : data.image,
                            isFavorite: !!data.isFavorite,
                            isLastUsed: !!data.isLastUsed,
                        }
                    ))
                const dbProjects = this.qb.getAllResult(resultSet).map((data) => ({
                    ...data,
                    isFavorite: !!data.isFavorite,
                    isLastUsed: !!data.isLastUsed,
                }));

                const incomingIds = new Set(projectsWithoutUrl.map((p) => p.id.toString()));
                const deleteProjects = idsAbsentFromIncoming(dbProjects, incomingIds);

                const addProjects = projects.filter((data, index) => {
                    const normalized = projectsWithoutUrl[index];
                    const existing = dbProjects.find((db) => db.id.toString() === data.id.toString());
                    if (!existing) {
                        return true;
                    }
                    return (
                        existing.name !== normalized.name ||
                        existing.number !== normalized.number ||
                        existing.image !== normalized.image ||
                        !!existing.isFavorite !== normalized.isFavorite ||
                        !!existing.isLastUsed !== normalized.isLastUsed
                    );
                });

                const addIds = new Set(addProjects.map((p) => p.id.toString()));
                const updateDeletes = dbProjects.filter(
                    (data) => addIds.has(data.id.toString()) && !deleteProjects.find((d) => d.id.toString() === data.id.toString())
                );
                const allDeleteProjects = [...deleteProjects, ...updateDeletes];

                addProjects.forEach((project) => {
                    allPromise.push(this.downloadProjectImages(project));
                });

                Promise.all(allPromise).then(downloadedProjects => {
                    const projectsWithFlags = downloadedProjects.map((project) => ({
                        ...project,
                        isFavorite: !!project.isFavorite,
                        isLastUsed: !!project.isLastUsed,
                    }));
                    resolve({projects: projectsWithFlags, deleteProjects: allDeleteProjects});
                });

            }, (e) => reject(e));
        })
    }

    public downloadProjectImages(project: Project): Promise<Project> {
        return new Promise<Project>((resolve) => {
            if (project.image) {
                const fileTransfer = new FileTransfer();
                fileTransfer.downloadFileFromURL(project.image, 'projectImages').then((image) => resolve({...project, ...{image: image}}));
            } else {
                resolve(project);
            }
        });
    }

    public getAll(): Promise<Project[] | undefined> {
        const db = this.SQLite;
        if (!db) return Promise.resolve(undefined);
        const { query, params } = this.qb.select('Project');
        return new Promise<Project[]>((resolve, reject) => {
            db.executeSql(query, params, (resultSet) => resolve(this.qb.getAllResult(resultSet)), (e) => reject(e));
        });
    }

    public upsertProject(project: Project) {
        if (!this.SQLite || !project) {
            return Promise.resolve();
        }
        return this.downloadProjectImages(project).then(row => new Promise((resolve, reject) => {
            this.SQLite.transaction((tx) => {
                const remove = this.qb.delete('Project', [{name: 'id', value: row.id}]);
                const insert = this.qb.insert('Project', [row]);
                tx.executeSql(remove.query, remove.params);
                tx.executeSql(insert.query, insert.params);
            }, (e) => reject(e), () => resolve(undefined));
        }));
    }

    public async deleteProjects(ids: Array<string | number>) {
        if (!this.SQLite || !ids.length) {
            return Promise.resolve();
        }
        const deletableIds: string[] = [];
        for (const id of ids) {
            const hasPending = await hasPendingSyncForProject(id);
            if (!hasPending) {
                deletableIds.push(String(id));
            }
        }
        if (!deletableIds.length) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            this.SQLite.transaction((tx) => {
                const remove = this.qb.deleteIn('Project', 'id', deletableIds);
                tx.executeSql(remove.query, remove.params);
            }, (e) => reject(e), () => resolve(undefined));
        });
    }

    public projectDetails(id) {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve(undefined);
        }
        let projectData;
        const where = [{name: 'id', value: id}];
        return new Promise<Project>((resolve) => {
            db.transaction((tx) => {
                const { query, params } = this.qb.select('Project', [], where);
                tx.executeSql(query, params, (_tx, result) => {
                    projectData = this.qb.getSingleResult(result);
                });
            }, () => resolve(projectData), () => {
                resolve({...projectData});
            });
        });
    }
}
